import { Suspense } from "preact/compat";
import { FunctionalComponent, h } from "preact";
import { useEffect } from "preact/hooks";
import { Route, Router } from "preact-router";
import { useTranslation } from "react-i18next";
import "broadcastchannel-polyfill";

import ErrorBoundary from "./errorBoundary";
import { lazyWithRetries } from "../utils/lazyWithRetries";

const Home = lazyWithRetries(() => import("../routes/home"));
const Payment = lazyWithRetries(() => import("../routes/payment"));
const Invoice = lazyWithRetries(() => import("../routes/invoice"));
const Card = lazyWithRetries(() => import("../routes/card"));
const BankAccount = lazyWithRetries(() => import("../routes/bankAccount"));
const NotFoundPage = lazyWithRetries(() => import("../routes/notfound"));
const Payout = lazyWithRetries(() => import("../routes/payout"));
const Billing = lazyWithRetries(() => import("../routes/billing"));
const BillingResult = lazyWithRetries(() => import("../routes/billingResult"));
const PaymentLinks = lazyWithRetries(() => import("../routes/paymentLinks"));
const EmbedResult = lazyWithRetries(() => import("../routes/embedResult"));
const NISamsungConnect = lazyWithRetries(() => import("../routes/samsungPay"));
const CareemPay = lazyWithRetries(() => import("../routes/careemPay"));
const HostedPages = lazyWithRetries(() => import("../routes/hostedPages"));
const Subscription = lazyWithRetries(() => import("../routes/subscription"));
const SubscriptionPlan = lazyWithRetries(
  () => import("../routes/subscriptionPlan")
);
const SubscriptionConfirmation = lazyWithRetries(
  () => import("../routes/subscriptionConfirmation")
);

import "../services/i18n";
import "../services/polyfills";
import "../analytics/analyticsManager";
import { SDKContextProvider, HeadlessSDK } from "../sdk";

const App: FunctionalComponent = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    document.dir = i18n.dir();
  }, [i18n, i18n.language]);

  return (
    <div id="preact_root">
      <ErrorBoundary i18n={i18n}>
        <SDKContextProvider>
          <Suspense fallback={null}>
            <Router>
              <Route path="/" component={Home} />
              <Route path="/embed/headless-sdk" component={HeadlessSDK} />
              <Route path="/embed/:id" component={Payment} />
              <Route path="/embed/payment/:id" component={Payment} />
              <Route path="/embed/payment/:id/result" component={EmbedResult} />
              <Route
                path="/embed/payment/:id/samsung-pay"
                component={NISamsungConnect}
              />
              <Route
                path="/embed/payment/:id/careem-pay"
                component={CareemPay}
              />
              <Route path="/embed/payout/:id" component={Payout} />
              <Route path="/link/invoice/:id" component={Invoice} />
              <Route path="/embed/card/:id" component={Card} />
              <Route path="/embed/bankaccount/:id" component={BankAccount} />
              <Route path="/link/billing/:id" component={Billing} />
              <Route path="/link/billing/result" component={BillingResult} />
              <Route path="/link/payment-link/:id" component={PaymentLinks} />
              <Route path="/embed/hpp/:id" component={HostedPages} />
              <Route path="/embed/subscription/:id" component={Subscription} />
              <Route
                path="/embed/subscription-plan/:id"
                component={SubscriptionPlan}
              />
              <Route
                path="/embed/subscription-confirmation/:subscriptionId"
                component={SubscriptionConfirmation}
              />
              {/* Mobile needs full page redirection to detect confirmation changes */}
              <Route
                path="/embed/mobile-sdk/confirmation"
                component={() => null}
              />
              <Route path="/not-found" component={NotFoundPage} />
              <NotFoundPage default />
            </Router>
          </Suspense>
        </SDKContextProvider>
      </ErrorBoundary>
    </div>
  );
};

export default App;
